import { IAIConfiguration } from "@entities/AIControls/lib/types";
import { ACCOUNT_STATUS, AdminUser, HistoryEntry, IPagination } from ".";
import { IBrandBilling } from "./brandBilling";
import { IBrandOrganisation } from "./brandOrganisation";

export type BrandPermissionEntry = {
  createdOn: string;
  updatedAt: string;
  userId: string;
  userType: string;
  permissions: string[];
  user: {
    _id: string;
    emailId: string;
    firstName: string;
    lastName: string;
    role: string;
    accountStatus: string;
    phoneNumber: string;
    countryCode: string;
    brandUserId: string;
    profilePicUrl?: string;
  };
  userModelName: string;
};

export interface Brands {
  _id: string;
  name: string;
  logoUrl: string;
  history: HistoryEntry;
  companyName: string;
  permission: BrandPermissionEntry[];
  type: BrandType;
  status: ACCOUNT_STATUS;
  isAgreement: boolean;
  guidelines: string;
  createdAt: string;
  updatedAt: string;
  __v: string;
  score: number;
  brandId: string;
  brandOrganisationName: string;
}

export interface GetBrands {
  data: Brands[];
  pagination: IPagination;
  facets: {
    accountManagers: {
      _id: string;
      count: number;
      firstName: string;
      lastName: string;
      brandIds: string[];
    }[];
    companyName: {
      _id: string;
      count: number;
    }[];
    paymentTerms: {
      _id: string;
      count: number;
    }[];
    type: {
      _id: BrandType;
      count: number;
    }[];
    status: {
      _id: ACCOUNT_STATUS;
      count: number;
    }[];
  };
}

export interface IGetOrganisationPermissions {
  createdOn: string;
  updatedAt: string;
  userId: string;
  userType: string;
  brandIds?: string[];
  permissions: string[];
  user: IUser;
  userModelName: string;
}

export interface IGetOrganisations {
  data: IBrandOrganisation[];
  pagination: IPagination;
}

export enum BrandType {
  Enterprise = "Enterprise",
  Mid_Market = "Mid Market",
  Others = "Others",
}

export type ICreateBrand = {
  name: string;
  logoUrl: string;
  type: BrandType;
  companyName: string;
};
interface BrandPermission {
  createdOn: string;
  updatedAt: string;
  userId: string;
  userType: string;
  permissions: string[];
  user: IBrandUser;
  userModelName: string;
}
export interface IBrandUser {
  createdOn: string;
  updatedAt: string;
  userId: string;
  userType: string;
  permissions: string[];
  user: IUser;
  userModelName: string;
  notes?: BrandNote;
}
export interface IUser {
  _id: string;
  emailId: string;
  firstName: string;
  lastName: string;
  role: string;
  accountStatus: string;
  phoneNumber: string;
  countryCode: string;
  profilePicUrl?: string;
  emailNotificationActive: boolean;
  permissions?: string[];
  demoMode?: boolean;
  isTestUser?: boolean;
  permissionGroups?: {
    _id: string;
    name: string;
  }[];
}

export interface IBrand extends Partial<IAIConfiguration> {
  _id: string;
  name: string;
  logoUrl: string;
  history: HistoryEntry[];
  companyName: string;
  permission: BrandPermission[];
  type: string;
  status: string;
  isAgreement: boolean;
  guidelines: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  aggreementExpireOn: string;
  aggreementNotes: string;
  aggreementUrl: string;
  paymentTerms: string;
  notes: BrandNote;
  brandId: string;
  brandUsers: IBrandUser[];
  adminUsers: IAdminUser[];
  note?: string;
  brandGuidelinesList?: string[];
  isBrandGuidelines?: boolean;
  performanceGuidelinesList?: string[];
  isPerformanceGuidelines?: boolean;
  isBrandLearnings?: boolean;
  websiteUrl?: string;
  brandOrganisationId: string;
  brandOrganisation?: IBrandOrganisation;

  billing?: IBrandBilling;
  linkedBilling?: IBrandBilling;
  id: string;
  isTestUser?: boolean;
  userPermissions: string[];
}
export interface IAdminUser extends IBrandUser {}
export interface ICreateBrandUser extends Omit<IUser, "_id" | "accountStatus"> {
  permissionGroupIds: string[];
}
export interface IEditBrandUser extends IUser {
  note: string;
  permissionGroupIds: string[];
}

interface BrandNote {
  createdBy: string;
  createdByModelName: string;
  note: string;
  createdByType: string;
  updatedBy: string;
  updatedByModelName: string;
  updatedByType: string;
}

export interface BrandUser {
  name: string;
  logoUrl: string;
  companyName: string;
  permission: {
    userId: string;
    user: Pick<AdminUser, "firstName" | "lastName" | "profilePicUrl">;
    permissions: string[];
  }[];
  history: HistoryEntry[];
  _id: string;
}

export interface IOrganisationFacets {
  industries: {
    _id: string;
    count: number;
  }[];
  type: {
    _id: string;
    count: number;
  }[];
  status: {
    _id: string;
    count: number;
  }[];
}

export interface IOrganisationBrandUser {
  _id: string;
  isTestUser?: boolean;
  emailId: string;
  firebaseRefId: string;
  firstName: string;
  lastName: string;
  brandId: string;
  history: HistoryEntry[];
  role: string;
  accountStatus: ACCOUNT_STATUS;
  phoneNumber: string;
  countryCode: string;
  emailNotificationActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  permissionGroups: {
    _id: string;
    name: string;
  }[];
  brands: {
    _id: string;
    name: string;
    userPermissions: string[];
  }[];
}

export interface IBrandUserRequest {
  brandOrganisationId: string;
  emailId: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  phoneNumber: string;
  role: string;
  emailNotificationActive?: boolean;
  brandIds?: string[];
}

export interface IBrandUserEditRequest extends Partial<IBrandUserRequest> {
  brandUserId: string;
  accountStatus?: ACCOUNT_STATUS;
  brandOrganisationId: string;
}

export interface IBrands {
  data: IBrand[];
  pagination: IPagination;
}
